import {
  Account,
  ActionType,
  CounterType,
  formatDateToMMDDYYYY,
  getDeviceAtTimeFromInstant,
  getScheduleDefaultInstant,
  getWallTimeInstant,
  Issue,
  IssueType,
  parseMMDDYYYYToDate,
  replaceMentionPlaceholderWithNames,
  RoutineType,
  SCHEDULE_DEFAULTS,
  Severity,
  useCreateMutation,
  useFindByQuery,
  useIsAssetRoutineIdpInProgress,
  useUpdateIssue,
  useUpdateMutation,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, View } from 'react-native';
import { Text } from 'react-native-paper';
import { mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { Spacing } from '../../styles';
import { formStyles } from '../../styles/forms';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { CardButton } from '../crop-circle/CardButton';
import { Toggle } from '../crop-circle/Components/Toggle/Toggle';
import { TypeIcon, TypeVariant } from '../crop-circle/Components/Type';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { IdpEditLockedHint } from '../routine/workflow/message/idpMessageStates';
import {
  ControlledTextInput,
  DatePickerSelector,
  SaveFormBottom,
} from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { CreatedDateField } from './createdDateField';
import { DeleteIssue } from './deleteIssue';
import { LabelSelector } from './labelSelector';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useRoleHex } from '../../themes/useRoleHex';

type IssueEditModalProps = {
  issueId: string;
  currentTitle: string;
  workspaceId?: string;
  currentLabels?: Array<{ id: string; name: string }>;
  /** Controlled labels driven by an external state owner (e.g. third panel). Set to undefined to use uncontrolled mode. */
  externalLabels?: Array<{ id: string; name: string }>;
  /** Called whenever labels change internally so parent can keep its state in sync. */
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
  currentAdjustCreationDate?: string;
  currentCreatedAt?: string;
  issue: Issue;
  onLabelSelectorEdit?: () => void;
  bottomBar: React.ComponentType<Record<string, unknown>>;
  onReassignPress?: () => void;
  /**
   * Renders the activity title + asset link block between Labels and Reminder
   * (Figma V6 activity options, node 9809:26476) and routes to the asset.
   * Mobile passes this; web does not, keeping the web column unchanged.
   */
  onAssetPress?: () => void;
  onEditCancel?: () => void;
  /** Called when form dirty/valid state changes, exposing save handler for parent-level navigation guards */
  onFormStateChange?: (
    isDirty: boolean,
    isValid: boolean,
    onSave: () => Promise<void>
  ) => void;
  onDeleted?: () => Promise<void>;
  onSaved?: () => void;
  onReviseSubmissionPress?: () => void;
  /** Optional node rendered at the end of the scroll content (web only, after form fields). */
  contentFooter?: React.ReactNode;
  /**
   * Campaign Issues render read-only by default. When true, pin-to-asset,
   * reminder and labels stay editable even for campaign Issues — the campaign
   * thread "settings" panel opts in and supplies its own save bar. Title, type,
   * reassign and adjust-creation-date remain campaign read-only.
   */
  allowCampaignMetadataEdit?: boolean;
};

type IssueEditFormData = {
  title: string;
  adjustCreationDate?: unknown;
  onReminderDate?: unknown;
};

export const IssueEdit: React.FC<IssueEditModalProps> = (
  props: IssueEditModalProps
) => {
  const { t } = useTranslation();
  // Placeholder grey for the dateless reminder time field — role-based so it
  // tracks the active theme rather than pinning one mode's hex.
  const placeholderHex = useRoleHex('textDisabled');

  // Locks the "Revise Submission" entry point while the submission's own
  // extraction is still running — plan 0027 Phase 3 "revise/edit entry
  // points". Reads `issue.assetRoutine.data` (never `routineState.state.idp`,
  // which is frozen at PENDING) and polls only while that snapshot is
  // non-terminal, so it re-enables live once the worker finishes.
  const isReviseSubmissionIdpLocked = useIsAssetRoutineIdpInProgress(
    props.issue?.assetRoutine
  );

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': props.issueId },
    };
  }, [props.issueId]);

  const { data: workflows } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: updateIssue } = useUpdateIssue();
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const [currentWorkflow, setCurrentWorkflow] = useState(
    workflows?.items?.[0] || null
  );

  const { labels: availableLabels } = useWorkspaceLabels(props.workspaceId);

  const formSchema = yupObject({
    title: string().required(),
    adjustCreationDate: mixed().optional(),
    onReminderDate: mixed().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      title: props.currentTitle ?? '',
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    },
  });

  useEffect(() => {
    const workflow = workflows?.items?.[0] || null;
    if (workflow && workflow !== currentWorkflow) {
      setCurrentWorkflow(workflow);
      if (workflow?.trigger?.config?.onDate) {
        setValue(
          'onReminderDate',
          formatDateToMMDDYYYY(workflow.trigger.config.onDate)
        );
      }
    }
  }, [workflows, currentWorkflow, setValue]);

  const reminderDateValue = useWatch({ control, name: 'onReminderDate' });

  // Reminder time-of-day picker. Backed by its own isolated react-hook-form
  // instance (not the main `control`), same pattern as issueReminderModal.tsx
  // and alertForm.tsx — keeps "has the user touched the pre-fill" a clean
  // per-field dirty check, independent of the main form's own dirty state.
  const existingAtTime = currentWorkflow?.trigger?.config?.atTime;
  const existingTimeZone = currentWorkflow?.trigger?.config?.timeZone;

  const scheduleTimeBaseline = useMemo(() => {
    if (existingAtTime && existingTimeZone) {
      return getWallTimeInstant(existingAtTime, existingTimeZone);
    }
    return getScheduleDefaultInstant(SCHEDULE_DEFAULTS.reminder);
  }, [existingAtTime, existingTimeZone]);

  const scheduleTimeForm = useForm<{ scheduleTime: string }>({
    defaultValues: { scheduleTime: scheduleTimeBaseline },
  });
  // Destructuring isDirty here (rather than only inside onSubmit) is what
  // makes RHF start computing it live — see [0028] rhf-isdirty memory.
  const {
    formState: { isDirty: isScheduleTimeDirty },
  } = scheduleTimeForm;

  useEffect(() => {
    scheduleTimeForm.reset({ scheduleTime: scheduleTimeBaseline });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleTimeBaseline]);

  const onSubmit = async (data: IssueEditFormData) => {
    const input = {
      id: props.issueId,
      title: data.title,
      severity: selectedSeverity,
      labels: selectedLabels.map((l) => ({ id: l.id })),
      adjustCreationDate: data.adjustCreationDate
        ? parseMMDDYYYYToDate(String(data.adjustCreationDate))
        : undefined,
      ...(pinChanged && { isPinned }),
    };

    await updateIssue({ input: input });

    if (typeof data.onReminderDate === 'string' && data.onReminderDate) {
      await createOrUpdateReminder(data.onReminderDate);
    }

    setLabelsChanged(false);
    setSeverityChanged(false);
    setPinChanged(false);
    props.onSaved?.();
  };

  const onEditCancel = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);
    setIsPinned(props.issue?.isPinned ?? false);
    setPinChanged(false);
    props.onLabelsChange?.(props.currentLabels || []);
    props.onEditCancel && props.onEditCancel();
  };

  const elementFocusCallBack = useAutoFocusRef();
  const [selectedLabels, setSelectedLabels] = React.useState<
    Array<{ id: string; name: string }>
  >(props.currentLabels || []);
  const [labelsChanged, setLabelsChanged] = React.useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity>(
    props.issue?.severity ?? Severity.Red
  );
  const [severityChanged, setSeverityChanged] = useState(false);
  const [isPinned, setIsPinned] = useState(props.issue?.isPinned ?? false);
  const [pinChanged, setPinChanged] = useState(false);

  useEffect(() => {
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
  }, [props.issueId, props.currentLabels]);

  useEffect(() => {
    if (props.externalLabels === undefined) return;
    setSelectedLabels(props.externalLabels);
    const serverIds = (props.currentLabels || [])
      .map((l) => l.id)
      .sort()
      .join(',');
    const externalIds = props.externalLabels
      .map((l) => l.id)
      .sort()
      .join(',');
    setLabelsChanged(serverIds !== externalIds);
  }, [props.externalLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReassignPress = () => {
    if (props.onReassignPress) {
      props.onReassignPress();
    }
  };

  const handleSeverityChange = (severity: Severity) => {
    setSelectedSeverity(severity);
    setSeverityChanged(severity !== (props.issue?.severity ?? Severity.Red));
  };

  const typeOptions = [
    {
      label: t('activity.filter.urgent'),
      value: Severity.Red,
      iconVariant: 'attention',
    },
    {
      label: t('activity.filter.pending'),
      value: Severity.Yellow,
      iconVariant: 'blocker',
    },
    {
      label: t('activity.filter.solved'),
      value: Severity.Green,
      iconVariant: 'solved',
    },
    {
      label: t('activity.filter.note'),
      value: Severity.Purple,
      iconVariant: 'note',
    },
  ];

  const handleLabelsChange = (
    newLabels: Array<{ id: string; name: string }>
  ) => {
    setSelectedLabels(newLabels);
    const currentLabelIds = (props.currentLabels || []).map((l) => l.id).sort();
    const newLabelIds = newLabels.map((l) => l.id).sort();
    const hasChanged =
      currentLabelIds.length !== newLabelIds.length ||
      currentLabelIds.some((id, index) => id !== newLabelIds[index]);
    setLabelsChanged(hasChanged);
    props.onLabelsChange?.(newLabels);
  };

  const handleCreateLabel = async (name: string) => {
    const result = await createLabel({
      input: { name, workspace: { id: props.workspaceId } },
    });
    return result;
  };

  const createOrUpdateReminder = async (onReminderDate: string) => {
    const workspaceId = { id: props.workspaceId };
    const config: Record<string, unknown> = {
      issueId: props.issueId,
      onDate: parseMMDDYYYYToDate(onReminderDate),
    };

    // Only write atTime/timeZone once the user has actually chosen a time — an
    // untouched pre-fill stays default-driven (no migration for existing
    // reminders). A reminder already saved with a time keeps sending it
    // verbatim rather than re-deriving from this session's device zone.
    if (isScheduleTimeDirty) {
      const scheduleTimeValue = scheduleTimeForm.getValues('scheduleTime');
      if (scheduleTimeValue) {
        const { atTime, timeZone } =
          getDeviceAtTimeFromInstant(scheduleTimeValue);
        config.atTime = atTime;
        config.timeZone = timeZone;
      }
    } else if (existingAtTime) {
      config.atTime = existingAtTime;
      config.timeZone = existingTimeZone;
    }

    const trigger = {
      type: 'ISSUE',
      config,
    };

    const action = {
      type: ActionType.Reminder,
    };

    const newReminderInput = {
      name: '',
      workspace: workspaceId,
      trigger: trigger,
      action: action,
    };

    const editReminderInput = {
      name: '',
      trigger: trigger,
      action: action,
      workspace: workspaceId,
    };

    if (workflows?.items?.[0]) {
      await updateReminder(editReminderInput);
    } else {
      await createReminder(newReminderInput);
    }
  };

  const createReminder = async (input: Record<string, unknown>) => {
    if (!props.issue.workspace?.id) {
      return;
    }

    const workflowInput = {
      ...input,
      workspace: {
        id: props.issue.workspace.id,
      },
    };

    await createWorkflow({ input: workflowInput });
  };

  const updateReminder = async (input: Record<string, unknown>) => {
    const updateInput = {
      ...input,
      id: workflows?.items?.[0].id,
    };

    await updateWorkflow({ input: updateInput });
  };

  const onResetForm = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);
    setIsPinned(props.issue?.isPinned ?? false);
    setPinChanged(false);
    props.onLabelsChange?.(props.currentLabels || []);
  };

  const combinedIsDirty =
    isDirty || labelsChanged || severityChanged || pinChanged;

  useEffect(() => {
    if (!props.onFormStateChange) return;
    props.onFormStateChange(combinedIsDirty, isValid, () =>
      handleSubmit(onSubmit)()
    );
  }, [combinedIsDirty, isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePinToggle = (value: boolean) => {
    setIsPinned(value);
    setPinChanged(value !== (props.issue?.isPinned ?? false));
  };

  // For campaign-typed issues we render the form in read-only mode: title and
  // createdAt are visible but disabled; type, reassign, adjust-creation-date and
  // the save/delete affordances are hidden entirely. The host (activityEditColumn
  // / CampaignThreadPanel) decides what to render in contentFooter — this
  // component just removes the in-form editable affordances. Pin, reminder and
  // labels are an exception when the host opts in via allowCampaignMetadataEdit
  // (the campaign thread settings panel, which supplies its own save bar).
  const isCampaign = props.issue?.type === IssueType.Campaign;
  const campaignMetadataReadOnly =
    isCampaign && !props.allowCampaignMetadataEdit;

  const renderReviseSubmissionSection = () => {
    if (Platform.OS !== 'web') return null;
    if (!props.onReviseSubmissionPress) return null;

    const isTallyCounter =
      props.issue.routineVersion?.type === RoutineType.Counter &&
      (props.issue.routineVersion?.config as any)?.counterType ===
        CounterType.Tally;

    if (props.issue.type !== IssueType.Routine || isTallyCounter) return null;

    const routineTitle =
      (props.issue.routineVersion?.config as any)?.title ||
      props.issue.routineVersion?.name ||
      '';

    return (
      <View style={formStyles.formItem}>
        <Text style={formStyles.formItemTitleCropCircle}>
          {t('issue.editModal.reviseSubmission')}
        </Text>
        <CardButton
          text={routineTitle}
          leftIcon="Routine"
          rightIcon="Chevron Right"
          showAvatars={false}
          onPress={
            isReviseSubmissionIdpLocked
              ? undefined
              : props.onReviseSubmissionPress
          }
          disabled={isReviseSubmissionIdpLocked}
          color="blue"
        />
        {isReviseSubmissionIdpLocked && (
          <IdpEditLockedHint
            style={styles.idpLockedHint}
            testID="revise-submission-idp-locked-hint"
          />
        )}
      </View>
    );
  };

  const BottomBar = props.bottomBar;

  // Activity title (mentions resolved) for the header block. Falls back to
  // the raw title when there are no mention placeholders. `userMentions` is
  // not on the generated Issue type, so it is read via a narrow cast to the
  // helper's own param type rather than `any`.
  const issueMentions = (props.issue as { userMentions?: Account[] })
    ?.userMentions;
  const processedTitle =
    props.issue?.title?.includes('@[') && issueMentions
      ? replaceMentionPlaceholderWithNames(
          props.issue.title,
          issueMentions,
          t('mentions.unknownUser'),
          true
        )
      : props.issue?.title;

  const pinSection = (
    <View style={formStyles.formItem}>
      <View style={styles.pinRow}>
        <Icon name="Pin" size={20} color={Colors.role.textStrong} />
        <View style={styles.pinContent}>
          <Text style={styles.pinTitle}>{t('issue.menu.pinToAsset')}</Text>
          <Text style={styles.pinSubtitle}>
            {t('issue.editModal.pinDescription')}
          </Text>
        </View>
        <Toggle
          value={isPinned}
          onValueChange={handlePinToggle}
          colorScheme="success"
          disabled={campaignMetadataReadOnly}
        />
      </View>
    </View>
  );

  // Activity title as the section label + asset link as a Card Button, matching
  // the Reassign field pattern (Figma V6 nodes 9809:26476, 9931:74242).
  // Rendered only when the host passes onAssetPress (mobile); web keeps its
  // existing column layout. Sits between Labels and Reminder.
  const titleAssetSection = props.onAssetPress && (
    <View style={formStyles.formItem}>
      <Text style={formStyles.formItemTitleCropCircle} numberOfLines={2}>
        {processedTitle}
      </Text>
      <CardButton
        text={props.issue?.asset?.name ?? props.issue?.workspace?.name ?? ''}
        showAvatars={false}
        bordered={false}
        showLeftIcon={false}
        onPress={props.onAssetPress}
      />
    </View>
  );

  // Like pinSection, this is placed per platform: web keeps its established
  // column order (Reassign directly under the title field), mobile moves it to
  // the bottom group just above Pin to Asset.
  const reassignSection = !isCampaign && (
    <View style={formStyles.formItem}>
      <View>
        <Text style={formStyles.formItemTitleCropCircle}>
          {t('issue.new.reassign')}
        </Text>
      </View>
      <CardButton
        text={props.issue?.asset?.name ?? ''}
        showAvatars={false}
        bordered={false}
        showLeftIcon={false}
        onPress={onReassignPress}
      />
    </View>
  );

  const modalContent = (
    <>
      {renderReviseSubmissionSection()}
      {/* Pin sits at the top on web (existing column layout) and at the
          bottom above Delete on mobile (Figma V6 activity options). */}
      {Platform.OS === 'web' && pinSection}
      {props.issue.type !== IssueType.Routine && !isCampaign && (
        <View style={formStyles.formItem}>
          <View>
            <Text style={formStyles.formItemTitleCropCircle}>
              {t('issue.new.type')}
            </Text>
          </View>
          <View style={styles.typeGrid}>
            {typeOptions.map((option) => (
              <Button
                key={option.value}
                variant="rounded"
                onPress={() => handleSeverityChange(option.value)}
                leftIcon={
                  <TypeIcon variant={option.iconVariant as TypeVariant} />
                }
                state={selectedSeverity === option.value ? 'active' : 'default'}
                style={{
                  backgroundColor:
                    selectedSeverity !== option.value
                      ? Colors.role.surfaceHover
                      : Colors.role.surfaceInverse,
                }}
              >
                {option.label}
              </Button>
            ))}
          </View>
        </View>
      )}
      {!!props.workspaceId && (
        <View style={formStyles.formItem}>
          <View>
            <Text style={formStyles.formItemTitleCropCircle}>
              {t('issue.new.labels')}
            </Text>
          </View>
          <LabelSelector
            selectedLabels={selectedLabels}
            availableLabels={availableLabels}
            onLabelsChange={handleLabelsChange}
            onCreateLabel={handleCreateLabel}
            onSelectorEdit={
              campaignMetadataReadOnly ? undefined : props.onLabelSelectorEdit
            }
            disabled={campaignMetadataReadOnly}
          />
        </View>
      )}
      {titleAssetSection}
      {/* Date + time two-up, matching the blackout-dates From/To row: labels
          sit inside each field so the pair reads as one control. The time is
          always rendered for discoverability but disabled until a date
          exists — without one nothing is written, so an editable time would
          be a promise the form can't keep. */}
      <View style={[formStyles.formItem, styles.reminderRow]}>
        <DatePickerSelector
          name="onReminderDate"
          setValue={setValue}
          control={control}
          label={t('issue.reminderDate')}
          placeholder="MM/DD/YYYY"
          hideCalendarIcon
          maxYears={5}
          minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
          disabled={campaignMetadataReadOnly}
          // When read-only, match the created-date/title disabled grey instead
          // of the lighter default opacity dimming.
          disabledTextColor={tokens.colors.system.textSecondary}
        />
        <DatePickerSelector
          mode="time"
          name="scheduleTime"
          control={scheduleTimeForm.control}
          setValue={scheduleTimeForm.setValue}
          label={t('issue.reminderTime')}
          placeholder="00:00"
          containerStyle={styles.reminderTimeField}
          disabled={campaignMetadataReadOnly || !reminderDateValue}
          // Two different disabled reasons want two different greys: with no
          // date the whole reminder pair should read as one unset unit
          // (placeholder grey), whereas a read-only campaign matches the
          // other locked fields like Created Date — which still carry the
          // fixed token, so this branch stays pinned to it rather than
          // drifting a shade away from the field it is meant to match.
          disabledTextColor={
            !reminderDateValue
              ? placeholderHex
              : tokens.colors.system.textSecondary
          }
        />
      </View>
      <View style={formStyles.formItem}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.new.title')}
          </Text>
        </View>
        <ControlledTextInput
          control={control}
          name="title"
          height={50}
          placeholder={t('issue.new.optionalPlaceholder')}
          disableInnerStyle={false}
          innerRef={Platform.OS === 'web' ? elementFocusCallBack : undefined}
          backgroundColor={Colors.role.surfaceHover}
          hideBorder={true}
          editable={!isCampaign}
          // Campaign titles are read-only; show the value in the standard muted
          // text grey rather than the lighter placeholder-grey disabled default.
          textColor={isCampaign ? Colors.role.textSecondary : undefined}
        />
      </View>
      {Platform.OS === 'web' && reassignSection}
      {!isCampaign && (
        <View style={formStyles.formItem}>
          <AdjustCreationDateField control={control} setValue={setValue} />
        </View>
      )}
      <View style={formStyles.formItem}>
        <CreatedDateField createdAt={props.currentCreatedAt || ''} />
      </View>
      {Platform.OS !== 'web' && reassignSection}
      {Platform.OS !== 'web' && pinSection}
    </>
  );

  return (
    <SaveFormBottom
      isDirty={combinedIsDirty}
      isValid={isValid}
      onSubmit={handleSubmit(onSubmit)}
      title={t('issue.editModal.header')}
      saveLabel={t('issue.editModal.save')}
      onCancel={onEditCancel}
      onResetForm={onResetForm}
      bottomBar={(barProps) => (
        <BottomBar {...barProps} isDirty={combinedIsDirty} />
      )}
    >
      {Platform.OS === 'web' ? (
        <ScrollView style={styles.formContainer}>
          {modalContent}
          {props.contentFooter}
        </ScrollView>
      ) : (
        <KeyboardSafeView style={styles.keyboardContainer}>
          <ScrollView scrollEnabled showsVerticalScrollIndicator>
            {modalContent}
            {props.onDeleted && (
              <View style={styles.deleteIssueContainer}>
                <DeleteIssue
                  issueId={props.issueId}
                  onDeleted={props.onDeleted}
                />
              </View>
            )}
          </ScrollView>
        </KeyboardSafeView>
      )}
    </SaveFormBottom>
  );
};

const styles = themedStyleSheet((role) => ({
  reminderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: 12,
  },
  // Cancels the time picker's default full-width so it shares the row evenly
  // with the date field (whose container already flexes). minWidth 0 is
  // required: flex items default to min-width auto, and the time input's
  // intrinsic width would otherwise claim more than half the row. The height
  // and zeroed vertical padding pin it to the 48px standard field box the
  // date picker renders, so the pair lines up top and bottom.
  reminderTimeField: {
    flex: 1,
    width: 'auto',
    minWidth: 0,
    height: 48,
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: 'center',
  },
  formContainer: {
    gap: 8,
    paddingBottom: Platform.OS === 'web' ? 16 : 400,
    paddingTop: 16,
    flexGrow: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  deleteIssueContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.smallPadding.paddingVertical,
    paddingBottom: Spacing.smallPadding.paddingVertical * 8,
    backgroundColor: role.surface,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pinContent: {
    flex: 1,
  },
  pinTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: role.textStrong,
  },
  pinSubtitle: {
    fontSize: 12,
    color: role.textSecondary,
    marginTop: 2,
  },
  // Typography/color come from IdpEditLockedHint; only spacing is local.
  idpLockedHint: {
    marginTop: tokens.spacing.xs,
  },
  mobileContainer: {
    backgroundColor: role.canvas,
    height: '100%',
  },
  inputContainer: {
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
}));

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) { id name }
  }
`;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
